import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { AuthImage } from "@/components/damages/AuthImage";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";

const blank = { vehicle_id: "", incident_date: todayISO(), driver_name: "", description: "", franchise_amount: "", status: "APERTA" };

export const DamageDialog = ({ damage, vehicles, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [files, setFiles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (damage) { setF({ ...blank, ...damage, franchise_amount: damage.franchise_amount ?? "" }); setPhotos(damage.photos || []); setFiles([]); }
  }, [damage]);
  if (!damage) return null;
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const locked = !isAdmin && damage.status === "CHIUSA";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, franchise_amount: f.franchise_amount === "" ? null : Number(f.franchise_amount) };
      const { data } = damage.id ? await api.put(`/damages/${damage.id}`, payload) : await api.post("/damages", payload);
      if (files.length) {
        const fd = new FormData();
        files.forEach((file) => fd.append("files", file));
        await api.post(`/damages/${data.id}/photos`, fd);
      }
      toast.success("Sinistro salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const removePhoto = async (p) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    try { await api.delete(`/damages/photos/${p.id}`); setPhotos(photos.filter((x) => x.id !== p.id)); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="damage-dialog">
        <DialogHeader>
          <DialogTitle>{damage.id ? `Sinistro ${damage.plate}` : "Nuovo sinistro"}</DialogTitle>
          <DialogDescription>{locked ? "Sinistro chiuso dall'amministrazione: sola lettura." : "Dati del mezzo, del driver e del danno."}</DialogDescription>
        </DialogHeader>
        <fieldset disabled={locked} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Mezzo</Label>
            <PlateCombobox value={f.vehicle_id} onChange={set("vehicle_id")} disabled={locked} testId="damage-vehicle-select"
              options={vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}${isAdmin ? ` · ${v.station_name}` : ""}` }))} />
          </div>
          <div className="space-y-1.5"><Label>Data sinistro</Label><Input type="date" max={todayISO()} value={f.incident_date} onChange={set("incident_date")} data-testid="damage-date-input" /></div>
          <div className="space-y-1.5"><Label>Driver alla guida</Label><Input value={f.driver_name} onChange={set("driver_name")} placeholder="Nome e cognome" data-testid="damage-driver-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Descrizione del danno</Label><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="damage-description-input" /></div>
        </fieldset>
        <div className="space-y-2">
          <Label>Foto del danno</Label>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {photos.map((p) => (
              <div key={p.id} className="relative">
                <AuthImage photoId={p.id} className="h-24 w-full cursor-zoom-in rounded-lg" onClick={() => setZoom(p.id)} testId={`damage-photo-${p.id}`} />
                {!locked && <button type="button" onClick={() => removePhoto(p)} className="absolute right-1 top-1 rounded-full bg-white/90 p-1 shadow" data-testid={`damage-photo-delete-${p.id}`}><X className="h-3 w-3" /></button>}
              </div>
            ))}
            {files.map((file) => <div key={file.name + file.size} className="grid h-24 place-items-center rounded-lg border border-dashed bg-slate-50 p-1 text-center text-[11px] text-slate-500">{file.name}<br />(da caricare)</div>)}
            {!locked && (
              <label className="grid h-24 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-slate-400" data-testid="damage-photo-add">
                <span className="flex flex-col items-center text-xs"><Camera className="mb-1 h-5 w-5" />Aggiungi foto</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} data-testid="damage-photo-input" />
              </label>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="grid gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Importo franchigia (€)</Label><Input type="number" step="0.01" min="0" value={f.franchise_amount} onChange={set("franchise_amount")} data-testid="damage-franchise-input" /></div>
            <div className="space-y-1.5"><Label>Stato pratica</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="damage-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="APERTA">Aperta</SelectItem><SelectItem value="CHIUSA">Chiusa</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        {!isAdmin && damage?.id && (
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm" data-testid="damage-franchise-readonly">
            <span className="text-slate-600">Franchigia (inserita dall'admin)</span>
            <b className="font-mono-num">{damage.franchise_amount == null ? "da inserire" : `€ ${Number(damage.franchise_amount).toFixed(2).replace(".", ",")}`}</b>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{locked ? "Chiudi" : "Annulla"}</Button>
          {!locked && <Button onClick={save} disabled={saving || !f.vehicle_id || !f.driver_name.trim() || f.description.trim().length < 3} data-testid="damage-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>}
        </DialogFooter>
        {zoom && (
          <div className="fixed inset-0 z-[100] grid place-items-center bg-black/80 p-4" onClick={() => setZoom(null)} data-testid="damage-photo-zoom">
            <AuthImage photoId={zoom} className="max-h-[90vh] max-w-full rounded-lg object-contain" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
